/**
 * @file Export settings service.
 * @description Handles export lock-duration settings, validation, and related history actions.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { apiFetch } from '../api/api';
import { API_ENDPOINTS } from '../constants/api';
import type {
  ExportSettings,
  ExportLog,
  ExportLogDaysResponse,
  ExportHistoryResponse,
  ExportSettingsResponse,
} from '../types/api/exportSettings';

export type {
  ExportSettings,
  ExportLog,
  ExportLogDaysResponse,
  ExportHistoryResponse,
  ExportSettingsResponse,
} from '../types/api/exportSettings';

export class ExportSettingsService {
  /**
   * Get export log days configuration
   */
  static async getExportLogDays(): Promise<ExportLogDaysResponse> {
    try {
      const response = await apiFetch<ExportLogDaysResponse>(
        API_ENDPOINTS.EXPORT_LOG_DAYS,
        'GET',
      );
      return response;
    } catch {
      throw new Error('Failed to fetch export log days');
    }
  }

  /**
   * Update export log days
   */
  static async updateExportLogDays(days: number): Promise<ExportLogDaysResponse> {
    try {
      const response = await apiFetch<ExportLogDaysResponse>(
        API_ENDPOINTS.EXPORT_LOG_DAYS,
        'POST',
        { days },
      );
      return response;
    } catch {
      throw new Error('Failed to update export log days');
    }
  }

  /**
   * Get export history/logs
   */
  static async getExportHistory(): Promise<ExportHistoryResponse> {
    try {
      const response = await apiFetch<ExportHistoryResponse>(
        API_ENDPOINTS.EXPORT_HISTORY,
        'GET',
      );
      return response;
    } catch {
      throw new Error('Failed to fetch export history');
    }
  }

  /**
   * Get export settings
   */
  static async getExportSettings(): Promise<ExportSettingsResponse> {
    try {
      const response = await apiFetch<ExportSettingsResponse>(
        `${API_ENDPOINTS.SETTINGS}/export-settings`,
        'GET',
      );
      return response;
    } catch {
      throw new Error('Failed to fetch export settings');
    }
  }

  /**
   * Update export settings
   */
  static async updateExportSettings(settings: Partial<ExportSettings>): Promise<ExportSettingsResponse> {
    try {
      const response = await apiFetch<ExportSettingsResponse>(
        `${API_ENDPOINTS.SETTINGS}/export-settings`,
        'POST',
        settings,
      );
      return response;
    } catch {
      throw new Error('Failed to update export settings');
    }
  }

  /**
   * Clear export history
   */
  static async clearExportHistory(): Promise<{ status: string; message: string }> {
    try {
      const response = await apiFetch<{ status: string; message: string }>(
        API_ENDPOINTS.EXPORT_HISTORY,
        'DELETE',
      );
      return response;
    } catch {
      throw new Error('Failed to clear export history');
    }
  }

  /**
   * Get available export log days options
   */
  static getLockDurationOptions(): { value: number; label: string }[] {
    return [
      { value: 5, label: '5 days' },
      { value: 7, label: '7 days' },
      { value: 10, label: '10 days' },
      { value: 15, label: '15 days' },
      { value: 30, label: '30 days' },
      { value: 60, label: '60 days' },
      { value: 90, label: '90 days' },
    ];
  }

  /**
   * Get default export settings
   */
  static getDefaultExportSettings(): ExportSettings {
    return {
      lockDurationDays: 7,
      allowedFormats: ['csv', 'excel'],
      maxFileSize: 10,
      autoDownload: true,
      includeHeaders: true,
      dateFormat: 'YYYY-MM-DD',
      timeZone: 'Asia/Kolkata',
    };
  }

  /**
   * Validate export settings
   */
  static validateExportSettings(settings: Partial<ExportSettings>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (settings.lockDurationDays && (settings.lockDurationDays < 1 || settings.lockDurationDays > 365)) {
      errors.push('Lock duration must be between 1 and 365 days');
    }

    if (settings.maxFileSize && (settings.maxFileSize < 1 || settings.maxFileSize > 1000)) {
      errors.push('Maximum file size must be between 1 and 1000 MB');
    }

    if (settings.allowedFormats && !Array.isArray(settings.allowedFormats)) {
      errors.push('Allowed formats must be an array');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Check if export log is expired
   */
  static isExportLogExpired(exportLog: ExportLog): boolean {
    const now = new Date();
    const expiresAt = new Date(exportLog.expires_at);
    return now > expiresAt;
  }

  /**
   * Get days until expiration
   */
  static getDaysUntilExpiration(exportLog: ExportLog): number {
    const now = new Date();
    const expiresAt = new Date(exportLog.expires_at);
    const diffTime = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}